import { useState, useEffect, useMemo, Fragment } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import AppLayout from '../components/AppLayout';
import { HeaderSearch } from '../components/HeaderControls';
import { Button } from '../components/ui';
import { useSettings } from '../hooks/useSettings';
import { fmtMoney } from '../utils/currency';
import {
  seedChartOfAccounts, addMissingSystemAccounts, accountBalances, type SeedResult,
} from '../utils/ledger';
import type {
  LedgerAccount, LedgerAccountType, NormalBalance, JournalEntry, BankAccount,
} from '../types';

// ── Helpers (duplicated per-page, matching this codebase's convention) ──────

const TYPE_ORDER: LedgerAccountType[] = ['asset', 'liability', 'equity', 'income', 'expense'];
const TYPE_LABEL: Record<LedgerAccountType, string> = {
  asset:     'Assets',
  liability: 'Liabilities',
  equity:    'Equity',
  income:    'Income',
  expense:   'Expenses',
};

function IconEmptyLedger() {
  return (
    <svg width="46" height="46" viewBox="0 0 46 46" fill="none" style={{ color: 'var(--text-3)' }}>
      <rect x="9" y="6" width="28" height="34" rx="2.5" stroke="currentColor" strokeWidth="2" />
      <path d="M15 6v34M15 15h22M15 23h22M15 31h22" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────

export default function ChartOfAccounts() {
  const { user, business } = useAuth();
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v ?? 0, settings.currency);

  const [accounts, setAccounts]   = useState<LedgerAccount[] | null>(null);
  const [entries, setEntries]     = useState<JournalEntry[]>([]);
  const [banks, setBanks]         = useState<BankAccount[]>([]);
  const [seeding, setSeeding]     = useState(false);
  const [seedMsg, setSeedMsg]     = useState('');
  const [seedErr, setSeedErr]     = useState('');

  useEffect(() => {
    const db = getClientDb();
    if (!db || !business) return;
    let repaired = false;
    const u1 = onSnapshot(
      collection(db, 'ledgerAccounts'),
      snap => {
        setAccounts(snap.docs.map(d => ({ id: d.id, ...d.data() } as LedgerAccount)));
        // Same quiet one-time top-up as useLedgerData (new system accounts).
        if (!repaired && !snap.empty) {
          repaired = true;
          addMissingSystemAccounts(db, business.id, new Set(snap.docs.map(d => d.id))).catch(() => {});
        }
      },
      () => setAccounts([]),
    );
    const u2 = onSnapshot(
      collection(db, 'journalEntries'),
      snap => setEntries(snap.docs.map(d => ({ id: d.id, ...d.data() } as JournalEntry))),
      () => {},
    );
    const u3 = onSnapshot(
      collection(db, 'bankAccounts'),
      snap => setBanks(snap.docs.map(d => ({ id: d.id, ...d.data() } as BankAccount))),
      () => {},
    );
    return () => { u1(); u2(); u3(); };
  }, [business]);

  const normalById = useMemo<Record<string, NormalBalance>>(() => {
    const m: Record<string, NormalBalance> = {};
    (accounts ?? []).forEach(a => { m[a.id] = a.normalBalance; });
    return m;
  }, [accounts]);

  const balances = useMemo(() => accountBalances(entries, normalById), [entries, normalById]);

  const [searchQ, setSearchQ] = useState('');
  const grouped = useMemo(() => {
    const by: Record<LedgerAccountType, LedgerAccount[]> = {
      asset: [], liability: [], equity: [], income: [], expense: [],
    };
    const q = searchQ.trim().toLowerCase();
    (accounts ?? [])
      .filter(a => !q || a.name.toLowerCase().includes(q) || (a.code ?? '').toLowerCase().includes(q))
      .forEach(a => { by[a.type]?.push(a); });
    for (const k of TYPE_ORDER) {
      by[k].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.code.localeCompare(b.code));
    }
    return by;
  }, [accounts, searchQ]);

  async function runSeed() {
    const db = getClientDb();
    if (!db || !business) return;
    setSeeding(true); setSeedMsg(''); setSeedErr('');
    try {
      const res: SeedResult = await seedChartOfAccounts(
        db, business.id,
        banks.map(b => ({ id: b.id, name: b.name, currency: b.currency, type: b.type, isActive: b.isActive })),
      );
      setSeedMsg(
        res.created > 0
          ? `Added ${res.created} account${res.created === 1 ? '' : 's'}.`
          : 'Chart of accounts is already complete — nothing to add.',
      );
    } catch (e) {
      setSeedErr(e instanceof Error ? e.message : 'Could not set up the chart of accounts.');
    }
    setSeeding(false);
  }

  const loading = accounts === null;
  const empty   = !loading && accounts!.length === 0;
  const isOwner = user?.role === 'owner';

  return (
    <AppLayout
      title="Chart of Accounts"
      subtitle="The categories every transaction is filed under — set up once, then it runs itself"
      toolbar={!empty && !loading ? (
        <HeaderSearch value={searchQ} onChange={setSearchQ} placeholder="Account name or code…" label="Search accounts" />
      ) : undefined}
      headerActions={
        isOwner && !empty ? (
          <Button variant="secondary" size="sm" onClick={runSeed} loading={seeding} style={{ height: 34, boxSizing: 'border-box' }}>
            Repair / Add Missing
          </Button>
        ) : undefined
      }
    >
      {(seedMsg || seedErr) && (
        <div className={seedErr ? 'error-msg' : 'info-msg'}>{seedErr || seedMsg}</div>
      )}

      {loading ? (
        <div className="card"><div className="skeleton" style={{ height: 240, borderRadius: 8 }} /></div>
      ) : empty ? (
        <div className="card">
          <div className="empty-state" style={{ padding: '48px 0', textAlign: 'center' }}>
            <IconEmptyLedger />
            <div className="empty-state-title" style={{ marginTop: 12 }}>No chart of accounts yet</div>
            <div className="empty-state-sub" style={{ maxWidth: 380, margin: '4px auto 0' }}>
              This is a one-time setup. It creates the standard set of categories
              for a diamond business, plus one for each of your bank accounts.
            </div>
            {isOwner ? (
              <div style={{ marginTop: 18 }}>
                <Button variant="primary" onClick={runSeed} loading={seeding}>
                  Set Up Chart of Accounts
                </Button>
              </div>
            ) : (
              <div className="empty-state-sub" style={{ marginTop: 14 }}>
                Ask the business owner to run the one-time setup.
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 90 }}>Code</th>
                  <th>Account</th>
                  <th style={{ textAlign: 'right', width: 160 }}>Balance</th>
                </tr>
              </thead>
              <tbody>
                {TYPE_ORDER.filter(t => grouped[t].length > 0).map(t => (
                  <Fragment key={t}>
                    <tr>
                      <td colSpan={3} style={{
                        fontSize: 11, fontWeight: 700, color: 'var(--text-3)',
                        textTransform: 'uppercase', letterSpacing: '0.06em',
                        background: 'var(--surface-2)',
                      }}>
                        {TYPE_LABEL[t]}
                      </td>
                    </tr>
                    {grouped[t].map(a => (
                      <tr key={a.id}>
                        <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{a.code}</td>
                        <td style={{ fontSize: 12, color: 'var(--text-2)' }}>
                          {a.name}
                          {a.currency && (
                            <span style={{ opacity: 0.5, marginLeft: 6, fontFamily: 'var(--font-num)', fontSize: 11 }}>
                              {a.currency}
                            </span>
                          )}
                        </td>
                        <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)', textAlign: 'right' }}>
                          {money(balances[a.id] ?? 0)}
                        </td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
